/**
 * Profile Helper Functions
 *
 * Utility functions for determining user roles in offices and managing
 * profile-related data transformations.
 */

import Database from '../types/Database'

/**
 * Extended Office type that includes the user's role in that office
 */
export interface OfficeWithRole extends Database.Office {
  userRole: 'owner' | 'manager' | 'member'
}

/**
 * Determines a user's role in a specific office
 *
 * @param office - The office to check the user's role in
 * @param user - The user whose role we're determining
 * @param officeManagers - Array of all office managers to check against
 * @returns The user's role: 'owner', 'manager', or 'member'
 */
export function determineUserRole(
  office: Database.Office,
  user: Database.User,
  officeManagers: Database.OfficeManager[]
): 'owner' | 'manager' | 'member' {
  console.log('=== DETERMINING USER ROLE ===')
  console.log('Office:', office.name, 'ID:', office.oid)
  console.log('User:', user.email, 'UID:', user.uid)
  console.log('Office Managers:', officeManagers)

  // Owner: user.uid matches office.uid (user created the office)
  if (office.uid === user.uid) {
    console.log('✓ User is OWNER (created the office)')
    return 'owner'
  }

  // Manager: user appears in officeManagers for this office
  // Check both userId (if set) and email (case-insensitive) for maximum compatibility
  const isManager = officeManagers.some(manager => {
    console.log('Checking manager:', manager)

    if (manager.officeId !== office.oid) {
      console.log(
        '  ✗ Office ID mismatch:',
        manager.officeId,
        '!==',
        office.oid
      )
      return false
    }

    // Check userId if it's set
    if (manager.userId && user.uid && manager.userId === user.uid) {
      console.log('  ✓ MATCH by userId:', manager.userId, '===', user.uid)
      return true
    }

    // Check email (case-insensitive comparison)
    if (manager.email && user.email) {
      const managerEmailLower = manager.email.toLowerCase()
      const userEmailLower = user.email.toLowerCase()
      console.log(
        '  Comparing emails:',
        managerEmailLower,
        'vs',
        userEmailLower
      )
      if (managerEmailLower === userEmailLower) {
        console.log('  ✓ MATCH by email')
        return true
      }
    }

    console.log('  ✗ No match')
    return false
  })

  if (isManager) {
    console.log('✓ User is MANAGER')
    return 'manager'
  }

  // Member: user has access but is not owner or manager
  console.log('✓ User is MEMBER (default)')
  return 'member'
}

/**
 * Checks if a user is the owner of a specific office
 *
 * @param office - The office to check ownership of
 * @param user - The user to check
 * @returns True if the user owns the office
 */
export function isOfficeOwner(
  office: Database.Office,
  user: Database.User
): boolean {
  return office.uid === user.uid
}

/**
 * Checks if a user is a manager of a specific office
 *
 * @param office - The office to check management status of
 * @param user - The user to check
 * @param officeManagers - Array of all office managers
 * @returns True if the user is a manager of the office
 */
export function isOfficeManager(
  office: Database.Office,
  user: Database.User,
  officeManagers: Database.OfficeManager[]
): boolean {
  return officeManagers.some(manager => {
    if (manager.officeId !== office.oid) {
      return false
    }

    // Check userId if it's set
    if (manager.userId && user.uid && manager.userId === user.uid) {
      return true
    }

    // Check email (case-insensitive comparison)
    if (manager.email && user.email) {
      return manager.email.toLowerCase() === user.email.toLowerCase()
    }

    return false
  })
}

/**
 * Transforms an array of offices into offices with role information
 *
 * @param offices - Array of offices to transform
 * @param user - The user whose roles we're determining
 * @param officeManagers - Array of all office managers
 * @returns Array of offices with role information
 */
export function addRolesToOffices(
  offices: Database.Office[],
  user: Database.User,
  officeManagers: Database.OfficeManager[]
): OfficeWithRole[] {
  return offices.map(office => ({
    ...office,
    userRole: determineUserRole(office, user, officeManagers)
  }))
}

/**
 * Gets role description text for display purposes
 *
 * @param role - The role to get description for
 * @returns Human-readable description of the role
 */
export function getRoleDescription(
  role: 'owner' | 'manager' | 'member'
): string {
  switch (role) {
    case 'owner':
      return 'Can manage the office and all its desks'
    case 'manager':
      return 'Can manage the office and its desks'
    case 'member':
      return 'Can book desks in the office'
    default:
      return 'Unknown role'
  }
}

/**
 * Gets role display name for UI purposes
 *
 * @param role - The role to get display name for
 * @returns Capitalized role name for display
 */
export function getRoleDisplayName(
  role: 'owner' | 'manager' | 'member'
): string {
  return role.charAt(0).toUpperCase() + role.slice(1)
}
